import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { API_URL } from '../../global/global';
import styled from 'styled-components';

const SkyAngelLogo = require('../../Images/skyangel.png');

interface DIDAvatarProps {
  onReady?: () => void;
  onError?: (error: string) => void;
  onSpeakingStart?: () => void;
  onSpeakingEnd?: () => void;
}

export interface DIDAvatarHandle {
  speak: (text: string) => Promise<void>;
  stopSpeaking: () => Promise<void>;
  startSession: () => Promise<void>;
  endSession: () => Promise<void>;
}

const DIDAvatar = forwardRef<DIDAvatarHandle, DIDAvatarProps>(({ onReady, onError, onSpeakingStart, onSpeakingEnd }, ref) => {
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [streamId, setStreamId] = useState<string | null>(null);
  const streamIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const [streamOpacity, setStreamOpacity] = useState(0);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const idleVideoRef = useRef<HTMLVideoElement>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const speakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  
  const PREVIEW_URL = "https://clips-presenters.d-id.com/v2/Amber/bV12ZVG_v_/pIH43p_2kZ/idle.mp4";

  // Manejar eventos del data channel
  const handleDataChannelMessage = (event: MessageEvent) => {
    const message = event.data;
    console.log('Data channel message:', message);
    
    if (message.startsWith('stream/started')) {
      console.log('Stream started - showing video');
      setStreamOpacity(1); // Stream visible
      setVideoIsPlaying(true);
    } else if (message.startsWith('stream/done')) {
      console.log('Stream done - showing idle');
      setStreamOpacity(0); // Idle visible (stream queda abajo)
      setVideoIsPlaying(false);
    }
  };

  useImperativeHandle(ref, () => ({
    speak: async (text: string) => {
      if (!streamIdRef.current || !sessionIdRef.current) {
        console.error('No active session');
        return;
      }
      
      // Si la conexión está desconectada, recrear stream
      if (peerConnectionRef.current?.iceConnectionState === 'disconnected' || 
          peerConnectionRef.current?.iceConnectionState === 'failed') {
        console.log('🔄 Reconnecting stream...');
        await initializeStream();
      }
      
      try {
        setIsSpeaking(true);
        onSpeakingStart?.();
        
        const response = await fetch(`${API_URL}/stream/${streamIdRef.current}/talk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('skybot_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ text, session_id: sessionIdRef.current })
        });
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error);
        }
        
        // El monitoreo automático detectará cuando empiece el video
        // y cambiará la opacity automáticamente
        
        // Calcular duración más generosa: 100 palabras por minuto + 3s buffer
        const words = text.split(' ').length;
        const durationMs = Math.max((words / 100) * 60 * 1000 + 3000, 5000);
        
        console.log(`⏱️ Duración estimada: ${durationMs}ms para ${words} palabras`);
        
        // Limpiar timeout anterior si existe
        if (speakingTimeoutRef.current) {
          clearTimeout(speakingTimeoutRef.current);
        }
        
        speakingTimeoutRef.current = setTimeout(() => {
          setIsSpeaking(false);
          onSpeakingEnd?.();
        }, durationMs);
      } catch (error) {
        console.error('Error speaking:', error);
        onError?.('Error al hacer hablar al avatar');
        setIsSpeaking(false);
        setStreamOpacity(0);
        onSpeakingEnd?.();
      }
    },
    stopSpeaking: async () => {
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
        speakingTimeoutRef.current = null;
      }
      
      // Eliminar el stream en D-ID para liberar la sesión
      if (streamIdRef.current && sessionIdRef.current) {
        try {
          await fetch(`${API_URL}/stream/${streamIdRef.current}/delete`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('skybot_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ session_id: sessionIdRef.current })
          });
        } catch (err) {
          console.error('Error deleting stream:', err);
        }
      }
      
      // Cerrar la conexión actual
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      setIsSpeaking(false);
      setStreamOpacity(0);
      setVideoIsPlaying(false);
      onSpeakingEnd?.();
      
      // Recrear la sesión inmediatamente
      await initializeStream();
    },
    startSession: async () => {
      await initializeStream();
    },
    endSession: async () => {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
      setSessionId(null);
    }
  }));

  const initializeStream = async () => {
    setIsLoadingSession(true);
    try {
      // Iniciar sesión de streaming
      const response = await fetch(`${API_URL}/stream/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skybot_token')}`
        }
      });
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Error iniciando stream');
      }

      const { sessionId: sid, streamId: strmId, offer, iceServers } = data;
      setSessionId(sid);
      setStreamId(strmId);
      streamIdRef.current = strmId;
      sessionIdRef.current = sid;
      console.log('✅ Stream session created:', strmId, 'session:', sid);

      // Configurar WebRTC
      const peerConnection = new RTCPeerConnection({ iceServers });
      peerConnectionRef.current = peerConnection;

      // Se configura data channel para recibir eventos
      peerConnection.ondatachannel = (event) => {
        const dataChannel = event.channel;
        dataChannelRef.current = dataChannel;
        console.log('Data channel received:', dataChannel.label);
        
        dataChannel.onmessage = handleDataChannelMessage;
        dataChannel.onopen = () => console.log('Data channel opened');
        dataChannel.onclose = () => console.log('Data channel closed');
      };

      peerConnection.ontrack = (event) => {
        console.log('✅ Stream ready', event.streams.length, 'streams');
        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
          videoRef.current.play().catch(e => console.error('Error playing video:', e));
        }
      };

      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('ICE candidate:', event.candidate);
          // Enviar ICE candidate a D-ID
          fetch(`${API_URL}/stream/${strmId}/ice`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('skybot_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              candidate: event.candidate.candidate,
              sdpMid: event.candidate.sdpMid,
              sdpMLineIndex: event.candidate.sdpMLineIndex,
              session_id: sid
            })
          });
        }
      };

      peerConnection.oniceconnectionstatechange = () => {
        console.log('ICE connection state:', peerConnection.iceConnectionState);
        
        if (peerConnection.iceConnectionState === 'disconnected' || 
            peerConnection.iceConnectionState === 'failed') {
          console.log('⚠️ Connection lost, will reconnect on next speak');
          setStreamOpacity(0);
          setVideoIsPlaying(false);
        }
      };

      // Set remote description
      await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

      // Create answer
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      // Send answer to backend
      await fetch(`${API_URL}/stream/${strmId}/sdp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skybot_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answer: peerConnection.localDescription,
          session_id: sid
        })
      });

      console.log('✅ WebRTC connection established');
      onReady?.(); // Avatar listo para usar
    } catch (error) {
      console.error('❌ Error initializing stream:', error);
      onError?.('Error iniciando sesión de avatar');
    } finally {
      setIsLoadingSession(false);
    }
  };

  return (
    <VideoContainer>
      <IdleVideo
        ref={idleVideoRef}
        src={PREVIEW_URL}
        autoPlay
        loop
        muted
        playsInline
      />
      <StreamVideo
        ref={videoRef}
        autoPlay
        playsInline
        muted={false}
        style={{ opacity: streamOpacity }}
      />
      <CornerLogo src={SkyAngelLogo} alt="SkyAngel" />
      {isSpeaking && <SpeakingIndicator>🗣️</SpeakingIndicator>}
    </VideoContainer>
  );
});

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const IdleVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
`;

const StreamVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 2;
  transition: opacity 0.8s ease-in-out;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  z-index: 3;
`;

const SpeakingIndicator = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 2rem;
  z-index: 3;
  animation: pulse 1s infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const CornerLogo = styled.img`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 200px;
  height: 200px;
  object-fit: contain;
  z-index: 3;

  @media (max-width: 768px) {
    display: none;
  }
`;

export default DIDAvatar;
